import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EagleLogo } from "@/components/EagleLogo";
import { useAuth } from "@/lib/auth";
import { isMuted, setMuted, playEagleCry } from "@/lib/sound";
import { ROLE_LABEL } from "@/lib/util";
import { Power, Bell, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { PageNav } from "@/components/PageNav";

const ROLE_HEADER = {
    worker:  "bg-brand-crew-header",
    manager: "bg-brand-foreman-header",
    admin:   "bg-brand-admin-header",
};
// Sign-out sat on its own header colour in every role -- contrast 1.55 on crew,
// 1.24 on foreman and 1.07 on admin, which is to say invisible, held together
// only by a faint border. Tan is the brand's secondary and clears every header
// (5.63 / 3.69 / 4.29), so the control reads as a button in all three.
// Per-role because gold reaches only 3.84 against the burnished-red admin
// header. Warm stone clears every header (10.65 navy / 9.32 charcoal /
// 5.43 red), so the control reads as a button in all three.
const ROLE_ACCENT = {
    worker:  "bg-brand-gold hover:bg-brand-gold-dark text-brand-navy",
    manager: "bg-brand-stone hover:bg-brand-gold text-brand-charcoal",
    admin:   "bg-brand-stone hover:bg-brand-gold text-brand-charcoal",
};

const ROLE_HOME = {
    worker:  "/crew",
    manager: "/foreman",
    admin:   "/admin",
};

export function AppHeader() {
    const { user, employee, logout } = useAuth();
    const roleLabel = employee ? ROLE_LABEL[employee.role] : null;
    const displayName = employee?.name || user?.name || user?.email || "";
    const [unread, setUnread] = useState(0);
    const [muted, setMutedState] = useState(isMuted);
    const headerBg = ROLE_HEADER[employee?.role] || "bg-brand-charcoal";
    const logoutBg = ROLE_ACCENT[employee?.role] || "bg-brand-gold hover:bg-brand-gold-dark text-brand-navy";
    const homePath = ROLE_HOME[employee?.role] || "/";

    useEffect(() => {
        if (!employee) return;
        const fetch = () =>
            api.get("/notifications/unread-count")
                .then((r) => setUnread(r.data.count || 0))
                .catch(() => {});
        fetch();
        const timer = setInterval(fetch, 60000);
        return () => clearInterval(timer);
    }, [employee]);

    return (
        <header className={`${headerBg} text-brand-bone shadow-lift sticky top-0 z-30`} data-testid="app-header">
            <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
                <Link to={homePath} className="flex items-center gap-3 flex-1 min-w-0 rounded-lg -mx-1 px-1 hover:bg-white/10 transition-colors" aria-label="Go to home screen" data-testid="header-home-link">
                    <EagleLogo className="h-10 w-10 shrink-0" disc={headerBg} />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-gold leading-none">
                            Talon Construction Company
                        </div>
                        <div className="flex items-baseline gap-2 mt-1 flex-wrap min-w-0">
                            {/* A real <h1>, not a styled span. The app had no
                                heading elements at all, which left every screen
                                without a document outline for a screen reader --
                                and left the slab-serif heading rule in index.css
                                matching nothing, so Roboto Slab never rendered
                                anywhere despite being loaded. */}
                            <h1 className="font-serif font-bold text-lg sm:text-xl truncate leading-none" data-testid="header-user-name">
                                {displayName}
                            </h1>
                            {roleLabel && (
                                <span
                                    data-testid="header-role-badge"
                                    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-brand-navy/40 border border-brand-gold/50 text-brand-bone"
                                >
                                    {roleLabel}
                                </span>
                            )}
                        </div>
                    </div>
                </Link>
                {/* The cry plays on the way in, so the only place to turn it
                    off is after it has already gone -- which is exactly when
                    someone wants to. One tap, on every screen, and it plays a
                    cry when switched back on so the tap confirms itself. */}
                <button
                    type="button"
                    onClick={() => {
                        const next = !muted;
                        setMuted(next);
                        setMutedState(next);
                        if (!next) playEagleCry();
                    }}
                    className="p-2 rounded-full hover:bg-brand-navy/30 shrink-0"
                    aria-label={muted ? "Turn the eagle cry on" : "Turn the eagle cry off"}
                    aria-pressed={muted}
                    title={muted ? "Sound off" : "Sound on"}
                    data-testid="sound-toggle"
                >
                    {muted ? <VolumeX className="h-5 w-5 opacity-60" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <Link
                    to="/notifications"
                    className="relative p-2 rounded-full hover:bg-brand-navy/30 shrink-0"
                    aria-label="Notifications"
                    data-testid="notifications-link"
                >
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        // Bone disc with red numerals, not a red disc: brand-danger IS
                        // the burnished red of the admin header, so a red badge vanished
                        // into it entirely. Bone clears all three (15.59 / 13.64 / 7.95).
                        <span
                            data-testid="unread-badge"
                            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-0.5 bg-brand-bone text-brand-danger text-[10px] font-bold rounded-full flex items-center justify-center leading-none"
                        >
                            {unread > 99 ? "99+" : unread}
                        </span>
                    )}
                </Link>
                <button
                    onClick={logout}
                    className={`inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-xl ${logoutBg} font-bold shrink-0 transition-colors border border-brand-charcoal/20`}
                    aria-label="Sign out"
                    data-testid="logout-button"
                >
                    {/* Was LogOut, whose arrow points right from the right edge of
                        the screen -- it read as "go forward", not "sign out", and
                        now that there is a real Back arrow just below it the
                        clash would be worse. Power carries no direction at all. */}
                    <Power className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest hidden sm:inline">Sign out</span>
                </button>
            </div>
            <div className="pb-1.5 -mt-1">
                <PageNav />
            </div>
        </header>
    );
}
